import { doc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Validate coupon and check all conditions
export const validateCoupon = async (couponCode, cartSubtotal, userId = null) => {
  try {
    if (!couponCode) {
      return { valid: false, error: 'Please enter a coupon code' };
    }

    // Find coupon by code (admin creates with random doc id and a 'code' field)
    const q = query(collection(db, 'coupons'), where('code', '==', couponCode.toUpperCase()));
    const snap = await getDocs(q);
    if (snap.empty) {
      return { valid: false, error: 'Invalid coupon code' };
    }

    const couponDoc = snap.docs[0];
    const coupon = { id: couponDoc.id, ...couponDoc.data() };

    // Check if coupon is active
    if (!coupon.isActive) {
      return { valid: false, error: 'This coupon is no longer active' };
    }

    // Check start date
    const startDate = coupon.startDate
      ? (typeof coupon.startDate.toDate === 'function'
        ? coupon.startDate.toDate()
        : new Date(coupon.startDate))
      : null;
    if (startDate && new Date() < new Date(startDate)) {
      return { 
        valid: false, 
        error: `This coupon will be valid from ${new Date(startDate).toLocaleDateString()}` 
      };
    }

    // Check expiry date
    // Handle both Firestore Timestamp and JS Date
    const expiryDate = coupon.expiryDate
      ? (typeof coupon.expiryDate.toDate === 'function'
        ? coupon.expiryDate.toDate()
        : new Date(coupon.expiryDate))
      : null;
    if (expiryDate && new Date(expiryDate) < new Date()) {
      return { valid: false, error: 'This coupon has expired' };
    }

    // Check minimum cart value
    if (coupon.minCartValue && cartSubtotal < coupon.minCartValue) {
      return { 
        valid: false, 
        error: `Minimum cart value of ₹${coupon.minCartValue} required` 
      };
    }

    // Check global usage limit
    const usedGlobal = typeof coupon.usedCount === 'number' ? coupon.usedCount : (coupon.currentUses || 0);
    if (coupon.maxUses && usedGlobal >= coupon.maxUses) {
      return { valid: false, error: 'This coupon has reached its usage limit' };
    }

    // Check per-user usage limit (if user is logged in)
    if (userId && coupon.maxUsesPerUser) {
      const userUsageCount = coupon.userUsage ? (coupon.userUsage[userId] || 0) : 0;
      if (userUsageCount >= coupon.maxUsesPerUser) {
        return { 
          valid: false, 
          error: 'You have already used this coupon the maximum number of times' 
        };
      }
    }

    return { valid: true, coupon };
  } catch (error) {
    console.error('Error validating coupon:', error);
    return { valid: false, error: 'Error validating coupon. Please try again.' };
  }
};

// Apply coupon and update usage count
export const applyCoupon = async (couponCode, userId = null) => {
  try {
    // Locate coupon document by code
    const q = query(collection(db, 'coupons'), where('code', '==', String(couponCode).toUpperCase()));
    const snap = await getDocs(q);
    if (snap.empty) {
      return { success: false, error: 'Invalid coupon code' };
    }
    const couponRef = doc(db, 'coupons', snap.docs[0].id);
    
    // Update global usage count
    const updateData = {
      usedCount: increment(1),
      currentUses: increment(1),
      lastUsed: new Date()
    };

    // Update per-user usage count if user is logged in
    if (userId) {
      updateData[`userUsage.${userId}`] = increment(1);
    }

    await updateDoc(couponRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error applying coupon:', error);
    return { success: false, error: 'Error applying coupon' };
  }
};

// Calculate discount amount based on coupon
export const calculateDiscountAmount = (coupon, subtotal) => {
  if (!coupon) return 0;

  if (coupon.type === 'percentage') {
    const discountAmount = subtotal * (coupon.value / 100);
    // Apply maximum discount limit if specified
    if (coupon.maxDiscount && discountAmount > coupon.maxDiscount) {
      return coupon.maxDiscount;
    }
    return discountAmount;
  } else if (coupon.type === 'fixed') {
    // Fixed amount discount cannot exceed subtotal
    return Math.min(coupon.value, subtotal);
  }

  return 0;
};

// Format coupon for display
export const formatCouponDisplay = (coupon) => {
  if (!coupon) return '';

  if (coupon.type === 'percentage') {
    return `${coupon.value}% OFF${coupon.maxDiscount ? ` (Max ₹${coupon.maxDiscount})` : ''}`;
  } else if (coupon.type === 'fixed') {
    return `₹${coupon.value} OFF`;
  }

  return '';
};
